import LabelWrapper from 'components/Forms/Lib/LabelWrapper/LabelWrapper';
import useTranslation from 'next-translate/useTranslation';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { ControllerRenderProps } from 'react-hook-form';
import SelectReact, {
    components,
    ControlProps,
    DropdownIndicatorProps,
    GroupBase,
    MenuListProps,
    MenuProps,
    OptionProps,
    PlaceholderProps,
    Props,
} from 'react-select';
import { twJoin } from 'tailwind-merge';
import { ExtractNativePropsFromDefault } from 'typeHelpers/ExtractNativePropsFromDefault';

declare module 'react-select/dist/declarations/src/Select' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
        label: string | JSX.Element;
        required?: boolean;
        hasError: boolean;
    }
}

type NativeProps = ExtractNativePropsFromDefault<
    Props,
    'options' | 'onChange',
    'defaultValue' | 'value' | 'isDisabled' | 'formatOptionLabel' | 'filterOption' | 'onFocus' | 'isLoading'
>;

type SelectProps = NativeProps & {
    id?: string | undefined;
    hasError: boolean;
    fieldRef?: ControllerRenderProps<any, any>;
    label: string | JSX.Element;
    required?: boolean;
    isSearchable?: boolean;
};

const customStyles = {
    indicatorSeparator: () => ({}),
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
    return (
        <components.DropdownIndicator
            aria-label={props.selectProps.label}
            className={twJoin(
                '!flex !h-12 !w-12 rotate-0 !items-center !justify-center !p-0 !transition-transform !duration-200 !ease-defaultTransition',
                props.selectProps.menuIsOpen && '!rotate-180',
            )}
            {...props}
        >
            <ArrowIcon className="h-4 w-4 text-grayDark" />
        </components.DropdownIndicator>
    );
};

const Control = (props: ControlProps) => {
    return (
        <LabelWrapper
            label={props.selectProps.label}
            required={props.selectProps.required}
            selectBoxLabelIsFloated={props.menuIsOpen === true || props.hasValue === true}
            htmlFor={props.selectProps.id + '_input'}
            inputType="selectbox"
        >
            <components.Control
                {...props}
                className={twJoin(
                    props.className,
                    'selectbox peer !z-two !min-h-[54px] !cursor-pointer !items-end !self-end !rounded-lg !border !border-solid !border-border !shadow-none !transition-none hover:!border-border',
                    (props.menuIsOpen as boolean) &&
                        '!items-end !rounded-b-[0] !border-borderFocus !shadow-select hover:!border-borderFocus',
                    (props.selectProps.hasError as boolean) &&
                        '!border-red !bg-white !shadow-redShadow hover:!border-red',
                )}
            />
        </LabelWrapper>
    );
};

const Menu = (props: MenuProps) => {
    return (
        <components.Menu
            className="!top-[calc(100%-1px)] !z-menu !m-0 !overflow-hidden !rounded-b-lg !border !border-solid !border-border !border-t-grayLighter !shadow-none"
            {...props}
        />
    );
};

const Placeholder = (props: PlaceholderProps) => {
    return <components.Placeholder className="!hidden" {...props} />;
};

const MenuList = (props: MenuListProps) => {
    return <components.MenuList className="!px-1 !pt-2" {...props} />;
};

const Option = (props: OptionProps) => {
    return (
        <components.Option
            {...props}
            aria-label={props.label}
            className={twJoin(
                '!mb-1 !cursor-pointer !rounded-md !p-2 !text-primary active:!bg-grayLighter',
                (props.isSelected || props.isFocused) && '!bg-grayLighter',
            )}
        />
    );
};

const Select: FC<SelectProps> = (props) => {
    const { t } = useTranslation();

    return (
        <SelectReact
            {...props}
            {...props.fieldRef}
            inputId={props.id + '_input'}
            classNamePrefix="select"
            styles={customStyles}
            placeholder={props.label}
            components={{ Control, DropdownIndicator, Menu, Option, MenuList, Placeholder }}
            isSearchable={props.isSearchable ?? false}
            noOptionsMessage={() => t('No options found')}
            hasError={props.hasError}
            label={props.label}
            required={props.required}
        />
    );
};

/* @component */
export default Select;
